import type { BoardQueueRow } from "../api/clinicalWorkspace";
import { TodayActionMenu } from "./TodayActionMenu";

/** The recorded outcome of a visit that ends without being completed. */
export type VisitCloseOutcome = "left" | "cancelled";
export type ClosableVisit = Pick<
  BoardQueueRow,
  "displayName" | "appointmentId" | "appointmentVersion" | "appointmentStatus" | "encounterSigned"
>;

const arrivedStatuses = ["waiting", "roomed", "in_progress", "ready_for_discharge"];

/**
 * Which closing outcomes the recorded stage allows, mirroring
 * api.transition_appointment: a patient who arrived can leave or have the
 * visit cancelled from any stage; a booked patient who never arrived can only
 * be cancelled. Empty for visits Today cannot close.
 */
export function visitCloseOutcomes(row: ClosableVisit): readonly VisitCloseOutcome[] {
  if (!row.appointmentId || row.appointmentVersion === undefined) return [];
  if (row.appointmentStatus === "scheduled") return ["cancelled"];
  return arrivedStatuses.includes(row.appointmentStatus ?? "") ? ["left", "cancelled"] : [];
}

/**
 * Closing a visit removes the patient from Today, so the choice is a second
 * step: the outcome is recorded explicitly and Keep visit backs out.
 */
export function TodayVisitCloser({
  row,
  disabled,
  onClose,
}: {
  readonly row: ClosableVisit;
  readonly disabled: boolean;
  readonly onClose: (outcome: VisitCloseOutcome) => void;
}) {
  const outcomes = visitCloseOutcomes(row);
  if (!outcomes.length) return null;
  const started =
    row.appointmentStatus === "in_progress" || row.appointmentStatus === "ready_for_discharge";
  return (
    <TodayActionMenu
      className="tb-visit-closer"
      label="Cancel visit"
      triggerLabel={`Cancel visit for ${row.displayName}`}
      disabled={disabled}
      menuLabel={`Close the visit for ${row.displayName}`}
      width={300}
    >
      {(menu) => (
        <>
          {outcomes.includes("left") ? (
            <button
              type="button"
              role="menuitem"
              disabled={disabled}
              onClick={() => {
                menu.close(true);
                onClose("left");
              }}
            >
              <strong>Patient left</strong>
              <span>
                {started
                  ? "The visit ends before it was finished."
                  : "The patient left without being seen."}
              </span>
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            disabled={disabled}
            onClick={() => {
              menu.close(true);
              onClose("cancelled");
            }}
          >
            <strong>Visit cancelled</strong>
            <span>
              {row.appointmentStatus === "scheduled"
                ? "The patient is not coming in."
                : "The visit was opened by mistake or called off."}
            </span>
          </button>
          <button type="button" role="menuitem" onClick={() => menu.close(true)}>
            <strong>Keep visit</strong>
          </button>
          {started && row.encounterSigned === false ? (
            <p>
              The note is unsigned. Closing the visit keeps the draft, but it can no longer be
              edited or signed. To document this visit instead, sign the note; signing marks the
              visit done.
            </p>
          ) : null}
        </>
      )}
    </TodayActionMenu>
  );
}
